import React, { createContext, useContext, useState, useEffect } from 'react';

const ProcessingContext = createContext();

// Scope processing state per user to prevent cross-account leaking
const getStorageKey = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('vlogclip_user') || '{}');
    if (userData.email) return 'vlogclip_processing_' + userData.email;
  } catch(e) {}
  return 'vlogclip_processing_anon';
};

export const useProcessing = () => {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
};

export const ProcessingProvider = ({ children }) => {
  const [processingState, setProcessingState] = useState({
    isProcessing: false,
    type: null,
    progress: { status: 'idle', step: '', progress: 0, message: '' },
    data: null,
    abortController: null,
    results: [],
    errors: []
  });

  // Load processing state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(getStorageKey());
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        const isCompleted = parsed.progress?.status === 'completed' || parsed.progress?.status === 'done' || parsed.progress?.status === 'error';
        setProcessingState({
          ...parsed,
          isProcessing: isCompleted ? false : parsed.isProcessing,
          abortController: null
        });
      } catch (error) {
        console.error('Failed to restore processing state:', error);
      }
    }
  }, []);

  // Save processing state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      ...processingState,
      abortController: null
    };
    try { localStorage.setItem(getStorageKey(), JSON.stringify(stateToSave)); } catch(e) { console.warn('localStorage full, state not persisted'); }
  }, [processingState]);

  // Cross-tab sync: listen for localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === getStorageKey() && e.newValue) {
        try {
          const newState = JSON.parse(e.newValue);
          setProcessingState(prev => ({
            ...newState,
            // Keep local abortController (only the originating tab has it)
            abortController: prev.abortController
          }));
        } catch (err) {
          console.error('Cross-tab sync error:', err);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const startProcessing = (type, data, abortController) => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => console.log('Notification permission:', p));
    }
    setProcessingState({
      isProcessing: true,
      type,
      progress: { status: 'processing', step: 'starting', progress: 0, message: 'Starting processing...' },
      data,
      abortController,
      results: [],
      errors: []
    });
  };

  const updateProgress = (progress) => {
    setProcessingState(prev => ({
      ...prev,
      progress
    }));
  };

  const updateResults = (results) => {
    setProcessingState(prev => ({
      ...prev,
      results
    }));
  };

  const updateErrors = (errors) => {
    setProcessingState(prev => ({
      ...prev,
      errors
    }));
  };

  const completeProcessing = (results = [], errors = []) => {
    setProcessingState(prev => ({
      ...prev,
      isProcessing: false,
      progress: { status: 'completed', step: 'done', progress: 100, message: 'Processing completed!' },
      results,
      errors,
      abortController: null
    }));

    // Browser notification when clips are ready
    try {
      let clipCount = 0;
      let videoCount = 0;
      if (Array.isArray(results)) {
        results.forEach(r => {
          if (r && r.clips && Array.isArray(r.clips)) {
            clipCount += r.clips.length;
            videoCount++;
          } else if (r && (r.file || r.videoUrl)) {
            clipCount++;
          }
        });
      }
      if (clipCount > 0 && document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        const body = videoCount > 1
          ? clipCount + ' clips from ' + videoCount + ' videos ready!'
          : clipCount + ' clip' + (clipCount !== 1 ? 's' : '') + ' generated successfully!';
        const n = new Notification('VlogClipAI - Clips Ready! 🎬', {
          body: body,
          icon: '/favicon.ico',
          tag: 'vlogclipai-complete',
          requireInteraction: true
        });
        n.onclick = () => { window.focus(); n.close(); };
        setTimeout(() => n.close(), 15000);
      }
    } catch(e) { console.log('Notification error:', e); }
  };

  const cancelProcessing = (jobId) => {
    if (processingState.abortController) {
      processingState.abortController.abort();
    }
    // Notify backend to cancel the job
    if (jobId) {
      try {
        const token = localStorage.getItem('auth_token');
        fetch('/api/batch/cancel/' + jobId, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        }).catch(() => {});
      } catch(e) {}
    }

    setProcessingState(prev => ({
      ...prev,
      isProcessing: false,
      progress: { status: 'idle', step: '', progress: 0, message: '' },
      abortController: null
    }));
    localStorage.removeItem(getStorageKey());
  };

  const resetProcessing = () => {
    setProcessingState({
      isProcessing: false,
      type: null,
      progress: { status: 'idle', step: '', progress: 0, message: '' },
      data: null,
      abortController: null,
      results: [],
      errors: []
    });
    localStorage.removeItem(getStorageKey());
  };

  const setAbortController = (controller) => {
    setProcessingState(prev => ({
      ...prev,
      abortController: controller
    }));
  };

  const value = {
    processingState,
    startProcessing,
    updateProgress,
    updateResults,
    updateErrors,
    completeProcessing,
    cancelProcessing,
    resetProcessing,
    setAbortController
  };

  return (
    <ProcessingContext.Provider value={value}>
      {children}
    </ProcessingContext.Provider>
  );
};
